import { api } from '@goaltree/backend/convex/_generated/api'
import { useConvexAuth, useQuery } from 'convex/react'

import { useUserMode } from '@/store/user-mode.store'

export const COACH_GREEN = '#419977'

/** Whether the signed-in user coaches anyone (active clients or open invites); undefined while loading. */
export function useIsCoach(): boolean | undefined {
  const { isAuthenticated, isLoading } = useConvexAuth()
  const count = useQuery(api.coaching.myClientCount, isAuthenticated ? {} : 'skip')
  const offers = useQuery(api.coachOffers.mine, isAuthenticated ? {} : 'skip')
  if (!isAuthenticated) return isLoading ? undefined : false
  if (count === undefined || offers === undefined) return count ? true : undefined
  return count > 0 || offers.length > 0
}

/** In coaching mode: only coaches can be; a stale persisted mode on a non-coach reads as personal. */
export function useIsCoaching(): boolean {
  const mode = useUserMode((s) => s.mode)
  const isCoach = useIsCoach()
  return mode === 'coaching' && isCoach !== false
}

// Personal pages outside coaching mode's navigation: opening one in coaching mode goes to /coaching.
const PERSONAL_ONLY = [
  '/today',
  '/everyday',
  '/inbox',
  '/upcoming',
  '/momentum',
  '/voice',
  '/review',
  '/stats',
]

// The coach's own goals list; /goals/$goalId stays open, it's where coaches edit client goals.
const isGoalsList = (pathname: string) => pathname === '/goals' || pathname === '/goals/'

export function isPersonalOnly(pathname: string): boolean {
  return (
    isGoalsList(pathname) ||
    PERSONAL_ONLY.some((p) => pathname === p || pathname.startsWith(`${p}/`))
  )
}

/** Where a personal page lands in coaching mode. */
export function coachingRedirect(pathname: string): '/coaching' | '/coaching/plans' {
  return isGoalsList(pathname) ? '/coaching/plans' : '/coaching'
}

export function switchMode(
  mode: 'personal' | 'coaching',
  navigate: (opts: { to: '/coaching' | '/today' }) => unknown,
) {
  useUserMode.getState().setMode(mode)
  void navigate({ to: mode === 'coaching' ? '/coaching' : '/today' })
}
